/**
 * muxRecoveryService — the client half of the "ask Mux directly"
 * rescue paths.
 *
 *   syncMuxStatus         → sync-mux-status         → delivery_files
 *   syncSiteMediaStatus   → sync-site-media-status  → studio_site_media  (FIX-3)
 *
 * Both exist because `mux-webhook` fails CLOSED when
 * MUX_WEBHOOK_SECRET is unset (SEC-1), which used to mean a video
 * could never leave "processing". These are the paths that finish the
 * job anyway. Neither one is a nice-to-have backstop — with the secret
 * missing they ARE the completion path.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

type SyncMuxParams = {
  deliveryId?: string;
  slug?: string;
  deliveryFileId?: string;
};

type SyncMuxResponse = {
  checked: number;
  updated: number;
  delivery_id: string;
};

export const syncMuxStatus = async ({ deliveryId, slug, deliveryFileId }: SyncMuxParams) => {
  if (!deliveryId && !slug) return null;

  const payload: Record<string, string> = {};
  if (deliveryId) payload.delivery_id = deliveryId;
  if (slug) payload.slug = slug;
  if (deliveryFileId) payload.delivery_file_id = deliveryFileId;

  const { data, error } = await supabase.functions.invoke('sync-mux-status', { body: payload });
  if (error) throw error;
  return data as SyncMuxResponse;
};

// ─────────────────────────────────────────────────────────────
// FIX-3 — studio_site_media (website videos)
// ─────────────────────────────────────────────────────────────

/** One reconciled row, as `sync-site-media-status` reports it. */
export type SiteMediaSyncRow = {
  id: string;
  mux_status: string;
  mux_playback_id: string | null;
};

/**
 * A named, actionable problem with ONE row. The function never hides
 * these in a 200-with-a-shrug — see its header for the code list
 * (mux_not_configured / no_mux_reference / asset_errored /
 * asset_missing / no_playback_id / mux_unreachable / db_error).
 */
export type SiteMediaSyncError = {
  id: string;
  code: string;
  detail: string;
};

export type SiteMediaSyncResponse = {
  checked: number;
  updated: number;
  site_id: string | null;
  media: SiteMediaSyncRow[];
  errors: SiteMediaSyncError[];
};

/**
 * Ask Mux for the truth about a website video and write it back to
 * `studio_site_media`. Pass `mediaId` for one row, `siteId` to sweep
 * every unsettled video on a site (max 20).
 *
 * Throws on transport / auth / configuration failure so callers can
 * distinguish "the rescue path is down" from "Mux isn't done yet".
 */
/**
 * Did `sync-site-media-status` answer 404? That answer is AUTHORITATIVE
 * — the function reads with the service role, so "not found" means the
 * row is genuinely gone, not that RLS hid it from us. A plain client
 * read can't tell those two apart, which is why we ask this instead of
 * guessing.
 *
 * supabase-js wraps a non-2xx in FunctionsHttpError with the raw
 * Response on `.context`. Everything here is defensive: an unexpected
 * error shape must return false, never throw.
 */
export const isSiteMediaNotFound = (err: unknown): boolean => {
  try {
    const status = (err as { context?: { status?: number } })?.context?.status;
    return status === 404;
  } catch {
    return false;
  }
};

export const syncSiteMediaStatus = async ({
  mediaId,
  siteId,
}: {
  mediaId?: string;
  siteId?: string;
}): Promise<SiteMediaSyncResponse | null> => {
  if (!mediaId && !siteId) return null;

  const payload: Record<string, string> = {};
  if (mediaId) payload.media_id = mediaId;
  if (siteId) payload.site_id = siteId;

  const { data, error } = await supabase.functions.invoke('sync-site-media-status', {
    body: payload,
  });
  if (error) throw error;
  return data as SiteMediaSyncResponse;
};

export const useMuxRecovery = ({
  deliveryId,
  slug,
  enabled = true,
  intervalMs = 15000,
}: {
  deliveryId?: string;
  slug?: string;
  enabled?: boolean;
  intervalMs?: number;
}) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!enabled || (!deliveryId && !slug)) return;

    let cancelled = false;

    const run = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const result = await syncMuxStatus({ deliveryId, slug });
        if (!result || cancelled || result.updated <= 0) return;

        if (deliveryId) {
          queryClient.invalidateQueries({ queryKey: ['delivery-files', deliveryId] });
          queryClient.invalidateQueries({ queryKey: ['public-delivery-files', deliveryId] });
        }
      } catch {
        // Silent fallback: webhook may still resolve status
      }
    };

    run();
    const interval = window.setInterval(run, intervalMs);

    const onVisible = () => run();
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [deliveryId, slug, enabled, intervalMs, queryClient]);
};
